import { useEffect, useState } from "preact/hooks";
import type { IndexRecord } from "../../shared/snapshot-format";
import type { CharacterSummary } from "../character";
import { createCharacter, deleteCharacter, duplicateStoredCharacter, getCharacter, listCharacters, pickCharacterFile, putCharacter, shareCharacter } from "../character";
import { subscribeRecords } from "../character/records";
import { loadCatalogue } from "../data/catalogue";
import { loadManifest } from "../data/manifest";
import { describeMigration, importCharacter, runSnapshotMigration, type MigrationRun } from "../migration";
import { appEngine, describeParkReason, timeStamp, type ConflictCopy, type ParkedRecord } from "../sync";
import { About } from "./about";
import { rememberSearch } from "./library";
import { RecentlyDeleted } from "./recently-deleted";
import { navigate } from "./router";
import { SyncSignIn } from "./sync-card";
import { SyncIndicator, useConflictCopies, useParked } from "./sync-status";
import { ThemeControl } from "./theme-control";
import { UpdateIndicator } from "./update-indicator";

let launchRun: Promise<MigrationRun> | undefined;

/** The Snapshot migration runner (#17), once per launch: every stored Character saved under another Snapshot is migrated before the list shows. */
export function migrateAtLaunch(): Promise<MigrationRun> {
  launchRun ??= runSnapshotMigration().catch((e: unknown) => ({ to: "", refused: `The Snapshot migration could not run: ${String(e)}`, migrated: [], failed: [] }));
  return launchRun;
}

/** "Snapshot pf2e-8.6.0: Valeros migrated (3 UUIDs redirected, 1 reopened)"; empty when nothing happened. */
export function describeRun(run: MigrationRun): string | undefined {
  if (run.refused) return run.refused;
  const parts = [...run.migrated.map((m) => `${m.name} migrated (${describeMigration(m.report)})`), ...run.failed.map((f) => `${f.name} could not be migrated: ${f.error}`)];
  return parts.length > 0 ? `Snapshot ${run.to}: ${parts.join("; ")}.` : undefined;
}

/**
 * The home screen (issue #14): every Character as a card with open, duplicate, export and delete;
 * New and Import; the way into the Library (#15); the Snapshot. Sync adds the sign-in card, the
 * Conflict Copies beside the Characters they lost to and Recently Deleted (#26).
 */
export function Home() {
  return (
    <div class="home">
      <header class="home-header">
        <h1>Pathfinder Offline</h1>
        <span class="actions">
          <SyncIndicator />
          <UpdateIndicator />
          <ThemeControl />
        </span>
      </header>
      <main class="home-body">
        <CharacterList />
        <SyncSignIn />
        <RecentlyDeleted />
        <LibraryEntry />
        <About />
      </main>
    </div>
  );
}

/**
 * Every Character, newest change first, plus New and Import. A Conflict Copy sits under the
 * Character it lost to with "Use this copy"; a Character the Account holds a version of that this
 * Device cannot apply yet says why (#26).
 */
export function CharacterList() {
  const [list, setList] = useState<CharacterSummary[]>();
  const [names, setNames] = useState<Map<string, string>>(new Map());
  const [message, setMessage] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState("");
  const copies = useConflictCopies();
  const parked = useParked();

  const refresh = () => listCharacters().then(setList, (e: unknown) => setMessage(`Could not read Characters: ${String(e)}`));
  useEffect(() => {
    migrateAtLaunch().then((run) => {
      const text = describeRun(run);
      if (text) setMessage(text);
      void refresh();
    });
    loadCatalogue().then((records) => setNames(new Map(records.map((r: IndexRecord) => [r.u, r.n]))), () => undefined);
    // A Character a sync brought in or took away shows up without the list being reopened (#25).
    return subscribeRecords((change) => {
      if (change.collection === "character") void refresh();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const run = async (work: () => Promise<string | undefined>) => {
    setBusy(true);
    setMessage(undefined);
    try {
      setMessage(await work());
    } catch (e) {
      setMessage((e as Error).message);
    } finally {
      setBusy(false);
      await refresh();
    }
  };

  const onCreate = () =>
    run(async () => {
      const trimmed = name.trim();
      if (!trimmed) return undefined;
      const manifest = await loadManifest();
      const c = await putCharacter(createCharacter(trimmed, manifest.snapshot.release));
      setNaming(false);
      setName("");
      navigate({ page: "character", id: c.id });
      return undefined;
    });

  const onImport = () =>
    run(async () => {
      const file = await pickCharacterFile();
      if (!file) return undefined;
      const { character, copied, report } = await importCharacter(await file.text());
      const migrated = report ? ` Migrated from Snapshot ${describeMigration(report)}.` : "";
      return (copied ? `Imported "${character.name}" as a copy; a Character with the same id already existed.` : `Imported "${character.name}".`) + migrated;
    });

  const onExport = (id: string) =>
    run(async () => {
      const c = await getCharacter(id);
      if (!c) return "That Character is gone.";
      const outcome = await shareCharacter(c, __APP_VERSION__);
      return outcome === "downloaded" ? `Saved a file for "${c.name}".` : outcome === "shared" ? "Shared." : undefined;
    });

  const onUseCopy = (copy: ConflictCopy) =>
    run(async () => {
      await appEngine().useConflictCopy(copy.id);
      return `"${copy.name}" now uses the version from ${copy.device}; the one that was here is kept as its Conflict Copy.`;
    });

  // A Conflict Copy is a Character of its own, so it comes back from `listCharacters` like any
  // other: it is lifted out of the run of cards and shown under the Character it lost to.
  const copyOf = new Map(copies.map((copy) => [copy.id, copy]));
  const under = new Map<string, ConflictCopy[]>();
  for (const copy of copies) under.set(copy.of, [...(under.get(copy.of) ?? []), copy]);
  const parkedOf = new Map(parked.filter((row) => row.collection === "character").map((row) => [row.id, row]));
  const held = new Set((list ?? []).map((s) => s.id));
  const cards = (list ?? []).filter((s) => !copyOf.has(s.id));
  // A copy whose Character was deleted here still has to be reachable.
  const orphans = copies.filter((copy) => !held.has(copy.of));

  const copyRow = (copy: ConflictCopy) => {
    const summary = (list ?? []).find((s) => s.id === copy.id);
    if (!summary) return null;
    return (
      <li key={copy.id} class="card character-card conflict-copy">
        <a href={`#/c/${encodeURIComponent(copy.id)}`} class="link">
          <strong>{copy.name}</strong>
          <span class="muted">
            <span class="tag warn">conflict, from {copy.device}, {timeStamp(copy.at)}</span>
          </span>
          <span class="muted">Level {summary.level}{summary.sessionOpen ? " · in play" : ""}</span>
        </a>
        <span class="actions">
          <button type="button" class="primary" disabled={busy} onClick={() => onUseCopy(copy)}>Use this copy</button>
          <button type="button" disabled={busy} onClick={() => onExport(copy.id)}>Export…</button>
          <DeleteButton name={summary.name} disabled={busy} onConfirm={() => run(async () => { await deleteCharacter(copy.id); return `Deleted the Conflict Copy of "${copy.name}".`; })} />
        </span>
      </li>
    );
  };

  return (
    <section class="characters-section">
      <div class="row">
        <h2>Characters</h2>
        <span class="actions">
          <button type="button" disabled={busy} onClick={onImport}>Import…</button>
          <button type="button" class="primary" disabled={busy} onClick={() => setNaming(!naming)}>New Character</button>
        </span>
      </div>
      {naming && (
        <form class="card new-character" onSubmit={(e) => { e.preventDefault(); void onCreate(); }}>
          <label>
            <span class="muted">Name</span>
            <input type="text" value={name} placeholder="e.g. Valeros" autoFocus onInput={(e) => setName((e.currentTarget as HTMLInputElement).value)} />
          </label>
          <span class="actions">
            <button type="submit" class="primary" disabled={busy || !name.trim()}>Create</button>
            <button type="button" disabled={busy} onClick={() => { setNaming(false); setName(""); }}>Cancel</button>
          </span>
        </form>
      )}
      {message && <p class="notice" role="status">{message}</p>}
      {!list ? (
        <p class="muted">Loading…</p>
      ) : list.length === 0 ? (
        <div class="card empty">
          <p>No Characters yet.</p>
          <p class="muted">Create one, or import a Character file exported from another device.</p>
        </div>
      ) : (
        <ul class="characters">
          {cards.map((s) => [
            <li key={s.id} class="card character-card">
              <a href={`#/c/${encodeURIComponent(s.id)}`} class="link">
                <strong>{s.name}</strong>
                <span class="muted">
                  Level {s.level}
                  {s.ancestryUuid ? ` · ${names.get(s.ancestryUuid) ?? "ancestry"}` : ""}
                  {s.classUuid ? ` · ${names.get(s.classUuid) ?? "class"}` : ""}
                  {s.sessionOpen ? " · in play" : ""}
                  {s.openChoices > 0 ? <> · <span class="tag open">{s.openChoices} open</span></> : null}
                </span>
                {parkedOf.has(s.id) ? <span class="muted"><span class="tag warn">a newer version is waiting: {describeParkReason(parkedOf.get(s.id)!.reason)}</span></span> : null}
              </a>
              <span class="actions">
                <button type="button" disabled={busy} onClick={() => run(async () => { await duplicateStoredCharacter(s.id); return undefined; })}>Duplicate</button>
                <button type="button" disabled={busy} onClick={() => onExport(s.id)}>Export…</button>
                <DeleteButton name={s.name} disabled={busy} onConfirm={() => run(async () => { await deleteCharacter(s.id); return `Deleted "${s.name}".`; })} />
              </span>
            </li>,
            ...(under.get(s.id) ?? []).map(copyRow),
          ])}
          {orphans.map(copyRow)}
        </ul>
      )}
    </section>
  );
}

/** Two-tap delete: the first tap arms, the second within a few seconds deletes. */
export function DeleteButton({ name, disabled, onConfirm }: { name: string; disabled?: boolean; onConfirm: () => void }) {
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    if (!armed) return;
    const t = setTimeout(() => setArmed(false), 4000);
    return () => clearTimeout(t);
  }, [armed]);
  return armed ? (
    <button type="button" class="danger" disabled={disabled} aria-label={`Confirm deleting ${name}`} onClick={() => { setArmed(false); onConfirm(); }}>
      Really delete?
    </button>
  ) : (
    <button type="button" disabled={disabled} aria-label={`Delete ${name}`} onClick={() => setArmed(true)}>Delete</button>
  );
}

/** The way into the Library (#15): a search that opens the browser on its results, and the Bookmarks and Recents. */
function LibraryEntry() {
  const [text, setText] = useState("");
  const go = (section: "browse" | "bookmarks" | "recents") => {
    if (section === "browse") rememberSearch(text.trim());
    navigate({ page: "library", section });
  };
  return (
    <section class="library-entry">
      <h2>Library</h2>
      <form class="inline" onSubmit={(e) => { e.preventDefault(); go("browse"); }}>
        <input type="search" aria-label="Search every Pack" placeholder="Search every Pack" value={text} onInput={(e) => setText((e.currentTarget as HTMLInputElement).value)} />
        <button type="submit" class="primary">Search</button>
      </form>
      <span class="actions">
        <a href="#/library" class="button">Browse Packs</a>
        <a href="#/library/bookmarks" class="button">Bookmarks</a>
        <a href="#/library/recents" class="button">Recents</a>
      </span>
    </section>
  );
}
